import type { Amp } from '../features/chat/chat.types';
import { apiFetch } from '../utils/apiClient';
 
const ICON_ID_MAP: Record<string, string> = {
  '0': 'scan',
  '1': 'zap',
  '2': 'file',
  '3': 'chart',
  '4': 'message',
  '5': 'phone',
  '6': 'database',
  '7': 'shield',
  '8': 'dollar',
  '9': 'search',
  '10': 'car',
  '11': 'compare',
  '12': 'image',
  '13': 'message',
};
 
const ICON_COLOR_MAP: Record<string, string> = {
  '0': '#06B6D4',
  '1': '#8B5CF6',
  '2': '#EC4899',
  '3': '#F59E0B',
  '4': '#10B981',
  '5': '#34D399',
  '6': '#14b8a6',
  '7': '#a855f7',
  '8': '#fbbf24',
  '9': '#22d3ee',
  '10': '#64748b',
  '11': '#fb923c',
  '12': '#ec4899',
  '13': '#8B5CF6',
};
 
const ICON_NAME_TO_ID: Record<string, string> = {
  scan: '0',
  zap: '1',
  file: '2',
  chart: '3',
  message: '4',
  phone: '5',
  database: '6',
  shield: '7',
  dollar: '8',
  search: '9',
  car: '10',
  compare: '11',
  image: '12',
};
 
// Fallback icon + color for premade AMPs whose icon is null in the API.
// Keys must match the `name` field returned by the premade AMPs endpoint.
const PREMADE_AMP_STYLE: Record<string, { icon: string; color: string }> = {
  // --- current API names ---
  'Acord Forms':                        { icon: 'file',     color: '#06b6d4' },
  'AI Calls':                           { icon: 'phone',    color: '#34d399' },
  'AMS Copilot':                        { icon: 'zap',      color: '#f97316' },
  'Certificates':                       { icon: 'file',     color: '#f97316' },
  'Commission Statements':              { icon: 'dollar',   color: '#fbbf24' },
  'Data Import':                        { icon: 'database', color: '#14b8a6' },
  'Documents':                          { icon: 'scan',     color: '#22d3ee' },
  'Driver Licenses':                    { icon: 'scan',     color: '#6366f1' },
  'General Support':                    { icon: 'message',  color: '#8b5cf6' },
  'InsuranceAgency.AI':                 { icon: 'message',  color: '#10B981' },
  'Leads & Prospects':                  { icon: 'message',  color: '#ec4899' },
  'Loss Runs':                          { icon: 'chart',    color: '#ef4444' },
  'Marketing Images':                   { icon: 'image',    color: '#ec4899' },
  'Motor Vehicle Records':              { icon: 'scan',     color: '#78716c' },
  'MVR Retrieval':                      { icon: 'car',      color: '#64748b' },
  'Personal Lines Data Enrichment':     { icon: 'database', color: '#0ea5e9' },
  'Policy Comparison':                  { icon: 'compare',  color: '#fb923c' },
  'Policy Summarizer':                  { icon: 'file',     color: '#2dd4bf' },
  'Policy Data Validator':              { icon: 'shield',   color: '#6366f1' },
  'Rate & Submission Builder':          { icon: 'zap',      color: '#84cc16' },
  'Renewals':                           { icon: 'zap',      color: '#6366f1' },
  'Reports':                            { icon: 'chart',    color: '#f59e0b' },
  'Risk Appetite':                      { icon: 'shield',   color: '#a855f7' },
  'WC Data Enrichment':                 { icon: 'database', color: '#14b8a6' },
  'Trucking Market Appetite':           { icon: 'car',      color: '#7c5cfc' },
  'My Carrier Finder/Risk Appetite':    { icon: 'search',   color: '#22c55e' },
  'Commission Forecast':                { icon: 'chart',    color: '#fbbf24' },
  'Easy 2 Quote':                       { icon: 'dollar',   color: '#10b981' },
  'Insured Property Data Enrichment':   { icon: 'database', color: '#0ea5e9' },
  'Quote to Bind':                      { icon: 'compare',  color: '#a78bfa' },
  'Smart Call Recording Summarization': { icon: 'phone',    color: '#fb923c' },
  'Edge':                               { icon: 'zap',      color: '#FEAD0F' },
};
 
export interface AmpApiResponse {
  id: string;
  title: string;
  description: string;
  category: string;
  system_prompt: string;
  icon_id: string;
  knowledge_base_path: string | null;
  created_at: string;
  updated_at: string;
}
 
export interface CreateAmpData {
  title: string;
  description: string;
  category: string;
  system_prompt: string;
  icon_id: string;
  knowledge_base_path: string | null;
}
 
function mapApiResponseToAmp(apiAmp: any, idPrefix: 'custom' | 'premade' = 'custom'): Amp {
  const rawId = apiAmp.id || apiAmp.amp_id || apiAmp._id || String(Date.now());
  // Namespace the ID to avoid collisions between custom and premade AMPs
  const ampId = `${idPrefix}:${String(rawId)}`;
 
  const name = apiAmp.name || apiAmp.title || 'Untitled AMP';
  const premadeStyle = PREMADE_AMP_STYLE[name];
 
  // icon: use API value if present, else look up by name in premade map, else fall back
  const iconName = apiAmp.icon
    ? (ICON_ID_MAP[apiAmp.icon] || apiAmp.icon)
    : (premadeStyle?.icon || ICON_ID_MAP[apiAmp.icon_id] || 'zap');
 
  const iconId = apiAmp.icon_id || ICON_NAME_TO_ID[iconName];
  const color = premadeStyle?.color || ICON_COLOR_MAP[iconId] || '#8B5CF6';
 
  return {
    id: ampId,
    name,
    description: apiAmp.description || '',
    category: apiAmp.category || 'Custom',
    icon: iconName,
    color,
    chatUrl: apiAmp.url || '/chat',
    instructions: apiAmp.prompt || apiAmp.system_prompt || undefined,
    knowledgeBase: apiAmp.rag || apiAmp.knowledge_base_path || undefined,
    createdAt: apiAmp.created_at || new Date().toISOString(),
    updatedAt: apiAmp.updated_at || new Date().toISOString(),
    prompt1: apiAmp.prompt1 || undefined,
    prompt2: apiAmp.prompt2 || undefined,
    prompt3: apiAmp.prompt3 || undefined,
    prompt4: apiAmp.prompt4 || undefined,
    type: apiAmp.type ?? undefined,
  };
}
 
function mapAmpToCreateData(amp: Partial<Amp>): CreateAmpData {
  return {
    title: amp.name || '',
    description: amp.description || '',
    category: amp.category || 'Support',
    system_prompt: amp.instructions || '',
    icon_id: ICON_NAME_TO_ID[amp.icon || 'zap'] || '1',
    knowledge_base_path: amp.knowledgeBase || null,
  };
}
 
// The AMP list endpoints (AMPManagement/list + /premade) are switched off: every
// caller gets an empty list and no request is sent. Flip to true to call them again
// — nothing else needs to change.
const AMP_LIST_ENDPOINTS_ENABLED = false;

export async function getAllAmps(): Promise<Amp[]> {
  if (!AMP_LIST_ENDPOINTS_ENABLED) return [];
  const data = await apiFetch<any>(`${process.env.REACT_APP_AccountAMP_Url}/api/AMPManagement/list`, {
    method: 'GET',
  });
 
  const list = Array.isArray(data) ? data : (data.amps || []);
  return list.map((a: any) => mapApiResponseToAmp(a, 'custom'));
}
 
export async function getPremadeAmps(): Promise<Amp[]> {
  if (!AMP_LIST_ENDPOINTS_ENABLED) return [];
  const data = await apiFetch<any>(`${process.env.REACT_APP_AccountAMP_Url}/api/AMPManagement/premade`, {
    method: 'GET',
  });
 
  const list = Array.isArray(data) ? data : (data.amps || []);
  return list.map((a: any) => mapApiResponseToAmp(a, 'premade'));
}
 
export async function getAmpById(id: string): Promise<Amp> {
  const apiAmp = await apiFetch<AmpApiResponse>(`${process.env.REACT_APP_MyAMP_Url}/api/amp/${id}`, {
    method: 'GET',
  });
 
  return mapApiResponseToAmp(apiAmp);
}
 
export async function createAmp(amp: Partial<Amp> & { shareWithCompany?: boolean }): Promise<Amp> {
  const rawId = amp.id ? amp.id.replace(/^[^:]+:/, '') : '0';
  const body = {
    id: parseInt(rawId, 10) || 0,
    name: amp.name || '',
    description: amp.description || '',
    icon: amp.icon || '',
    prompt: amp.instructions || '',
    rag: amp.knowledgeBase || '',
    shareWithCompany: amp.shareWithCompany ?? true,
  };
 
  const apiAmp = await apiFetch<any>(`${process.env.REACT_APP_AccountAMP_Url}/api/AMPManagement/upsert`, {
    method: 'POST',
    body: JSON.stringify(body),
    skipCache: true,
    headers: { 'Nc-Token': localStorage.getItem('amsToken') || '' }
  });
 
  return mapApiResponseToAmp(apiAmp, 'custom');
}
 
export async function updateAmp(id: string, amp: Partial<Amp>): Promise<Amp> {
  const updateData = mapAmpToCreateData(amp);
 
  const apiAmp = await apiFetch<any>(`${process.env.REACT_APP_MyAMP_Url}/api/amp/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
    skipCache: true,
  });
 
  return mapApiResponseToAmp(apiAmp);
}
 
export async function deleteAmp(id: string): Promise<void> {
  const rawId = id.includes(':') ? id.split(':').slice(1).join(':') : id;
  await apiFetch<void>(`${process.env.REACT_APP_AccountAMP_Url}/api/AMPManagement/delete/${rawId}`, {
    method: 'DELETE',
    skipCache: true,
  });
}
 
export async function getSuggestedQuestions(ampId: string, query: string = ''): Promise<string[]> {
  // Strip namespace prefix (e.g. "premade:1" -> "1", "custom:abc" -> "abc")
  const rawId = ampId.includes(':') ? ampId.split(':').slice(1).join(':') : ampId;
  const params = new URLSearchParams({ amp_id: rawId });
  if (query) params.set('q', query);
  const data = await apiFetch<{ suggestions: string[] }>(
    `${process.env.REACT_APP_MyAMP_Url}/api/amp-suggested-questions?${params.toString()}`,
    { method: 'GET' }
  );
  return data.suggestions || [];
}
 
export async function generatePrompt(userMessage: string): Promise<string> {
  const data = await apiFetch<{ generated_prompt?: string }>(
    `${process.env.REACT_APP_MyAMP_Url}/api/generate-prompt?user_message=${encodeURIComponent(userMessage)}`,
    {
      method: 'GET',
    }
  );
 
  return data.generated_prompt || '';
}
 